// 定义 API 基础地址，实际开发中可以从环境变量中获取
export const API_BASE_URL = 'https://ihavegoodideas.cn';

/**
 * 获取 Token
 * @param {string} account - 用户输入的 account
 * @param {string} password - 用户输入的密码
 * @returns {Promise<{access_token: string, refresh_token: string}>}
 */
export const loginWithAccount = async (account, password) => {
  const url = `${API_BASE_URL}/auth/v1/login/phone`;
  
  console.log(`Sending request to ${url} with account: ${account}`);

  const response = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ 
    "phone_num":account,
    "password":password }),
  });

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  const data = (await response.json()).data;
  
  if (!data.access_token || !data.refresh_token) {
    throw new Error('响应中未包含完整的 Token 信息 (access_token, refresh_token)');
  }

  return data;
};

/**
 * 刷新 Token (预留接口)
 * @param {string} 
 */
export const refreshToken = async () => {
    const refreshToken = getRefreshToken();
    const url = `${API_BASE_URL}/auth/v1/login/refresh`;
    const response = await fetch(url, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${refreshToken}`,
    }});
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = (await response.json()).data;
    if (!data.access_token) {
      throw new Error('Refresh token response missing access_token');
    }
    
    saveTokens(data.access_token, data.refresh_token);
    return data;
};

/**
 * 检查 Token 状态
 * @returns {Promise<any>}
 */
export const checkToken = async () => {
  const result = await authenticatedFetch('/auth/v1/login/check', 'GET');
  // 如果后端 /check 接口返回了用户 ID，也可以在这里处理
  // if (result) {
  //   localStorage.setItem('user_id', result);
  // }
  return result;
};

/**
 * 获取当前用户信息（包含 UUID、头像、昵称等）
 * 这需要后端提供一个配套的接口，例如 GET /auth/v1/user/info
 * @returns {Promise<any>}
 */
export const getCurrentUserInfo = async (phoneNum) => {
  const userInfo = await authenticatedFetch(`/auth/v1/user/query_user_info?phone=${phoneNum}`, 'GET');
  if (userInfo && userInfo.uid) {
      localStorage.setItem('user_id', userInfo.uid);
  }
  return userInfo;
};

export const getCurrentUserId = () => {
  return localStorage.getItem('user_id');
}

/**
 * 根据手机号搜索用户
 * @param {string} phoneNum 
 * @returns {Promise<any>}
 */
export const searchUserByPhone = async (phoneNum) => {
    return await authenticatedFetch(`/auth/v1/user/query_user_info?phone=${phoneNum}`, 'GET');
};

export const searchUserByUid = async (uid) => {
  return await authenticatedFetch(`/auth/v1/user/query_user_info?uid=${uid}`, 'GET');
};

/**
 * 创建手机号用户
 * @param {string} phoneNum 
 * @param {string} password 
 * @returns {Promise<any>}
 */
export const createPhoneUser = async (phoneNum, password) => {
  return await authenticatedFetch('/auth/v1/user/create_phone_user', 'POST', {
    phone_num: phoneNum,
    password: password
  });
};

export const saveAccount = (account) =>{
  if (account) {
    localStorage.setItem('account', account);
  }
}

export const getCurrentUserAccount = () => {
  return localStorage.getItem('account');
}

/**
 * 保存 Token 到本地存储
 * @param {string} accessToken 
 * @param {string} refreshToken 
 */
export const saveTokens = (accessToken, refreshToken) => {
    if (accessToken) localStorage.setItem('access_token', accessToken);
    if (refreshToken) localStorage.setItem('refresh_token', refreshToken);
};

export const getAccessToken = () => {
  return localStorage.getItem('access_token');
};

export const getRefreshToken = () => {
  return localStorage.getItem('refresh_token');
};

/**
 * 从本地存储清除 Token
 */
export const clearTokens = () => {
  localStorage.removeItem('access_token');
  localStorage.removeItem('refresh_token');
  localStorage.removeItem('user_id');
  localStorage.removeItem('account');
};

/**
 * 通用认证请求包装器
 * 自动处理 Token 添加、401重试和 Token 刷新
 * @param {string} url - 请求路径 (可以是相对路径或绝对路径)
 * @param {string} method - HTTP 方法 (GET, POST, PUT, DELETE 等)
 * @param {object} [body] - 请求体数据 (可选)
 * @returns {Promise<any>} - 返回响应数据的 .data 部分
 */
export const authenticatedFetch = async (url, method = 'GET', body = null) => {
  // 如果 url 不以 http 开头，且不以此服务的基础 URL 开头（避免重复拼接），则拼接 API_BASE_URL
  const fullUrl = url.startsWith('http') ? url : `${API_BASE_URL}${url}`;
  
  const getOptions = (token) => {
    const headers = {
      'Content-Type': 'application/json',
    };
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    
    const options = {
      method,
      headers,
    };

    if (body) {
      options.body = JSON.stringify(body);
    }
    return options;
  };

  try {
    // 1. 第一次尝试请求
    let accessToken = getAccessToken();
    let response = await fetch(fullUrl, getOptions(accessToken));

    // 2. 如果响应 401，尝试刷新 Token
    if (response.status === 401) {
      console.log('Access token expired or invalid, attempting refresh...');
      try {
        // 刷新 Token (会自动保存到 localStorage)
        const refreshResult = await refreshToken();
        const newAccessToken = refreshResult.access_token;
        
        // 3. 使用新 Token 重试请求
        console.log('Token refreshed, retrying request...');
        response = await fetch(fullUrl, getOptions(newAccessToken));

      } catch (refreshError) {
        console.error('Token refresh failed:', refreshError);
        // 刷新失败，清除本地 Token (用户可能需要重新登录)
        clearTokens();
        throw new Error('会话已过期，请重新登录');
      }
    }

    if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
    }

    // 这里假设后端返回的标准结是 { code: 0, msg: "success", data: ... } 或者是直接的数据
    // 根据 loginWithUuid 的实现，这里似乎期望直接返回 data 字段
    const jsonResponse = await response.json();
    return jsonResponse.data !== undefined ? jsonResponse.data : jsonResponse;

  } catch (error) {
    console.error(`Request failed: ${method} ${url}`, error);
    throw error;
  }
};
